import React, { useState, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useMutation } from "@apollo/client";
import { message } from "antd";
import { Role_Management } from "../../../services/userAPI";
import toggleImage01 from "../../../Images/toggles-fill-image01.png";
import NextIcon from "../../../Images/nextIcon.png";
import "../../Dashboard/Darts/masterConfiguration/productConfiguration/productConfiguration.css";
import AdminTargetFunnelConfiguration from "./adminTargetFunnelConfiguration";
import AdminTargetFunnelPreview from "./adminTargetFunnelPreview";
import AdminLeadIndicatorSelection from "./adminLeadIndicatorSelection";
import PageBreadcrumb from '../../../reusableField/PageBreadcrumb';

const AdminProductConfiguration = () => {
    const location = useLocation();
    const navigate = useNavigate();

    const dartsFormNeeded = location.state?.dartsFormNeeded;
    const accountId = location.state?.accountId;
    const roleId = location.state?.roleId;
    const pocName = location.state?.pocName;
    const pocEmail = location.state?.pocEmail;
    const pocNumber = location.state?.pocNumber;
    const numberLogin = location.state?.numberLogin;
    const emailLogin = location.state?.emailLogin;
    const mandatoryTimeDistribution = location.state?.mandatoryTimeDistribution;
    const targetEditDays = location.state?.targetEditDays;
    const inputEditDays = location.state?.inputEditDays;
    const name = location.state?.name;
    const dartsNeeded = location.state?.dartsNeeded;
    const clanNeeded = location.state?.clanNeeded;
    const syncNeeded = location.state?.syncNeeded;
    const accountName = location.state?.accountName;
    const geoTracking = location.state?.geoTracking;
    const signInSignOutRequired = location.state?.signInSignOutRequired;
    const locationRequired = location.state?.locationRequired;
    const jobroleAccessUserIds = location.state?.jobroleAccessUserIds;
    const ldmsNeeded = location.state?.ldmsNeeded;
    const mirrorNeeded = location.state?.mirrorNeeded;

    const [inputType, setInputType] = useState(null);
    const [initialInputType, setInitialInputType] = useState(null);
    const [currentView, setCurrentView] = useState("main");
    const [loading, setLoading] = useState(false);
    const [errorMessage, setErrorMessage] = useState("");
    const [isSaveDisabled, setIsSaveDisabled] = useState(true);
    const [isNextDisabled, setIsNextDisabled] = useState(true);
    const [targetData, setTargetData] = useState(null);
    const [leadIndicatorData, setLeadIndicatorData] = useState(null);

    const [addJobRole] = useMutation(Role_Management);

    useEffect(() => {
        if (dartsFormNeeded !== undefined) {
            const newInputType = dartsFormNeeded ? "Forms" : "Numbers";
            setInputType(newInputType);
            setInitialInputType(newInputType);
            setIsSaveDisabled(true);
            setIsNextDisabled(false);
        } else {
            console.warn("dartsFormNeeded is undefined, resetting inputType");
            setInputType(null);
            setInitialInputType(null);
            setIsSaveDisabled(true);
            setIsNextDisabled(true);
        }
    }, [dartsFormNeeded]);

    const handleInputChange = (event) => {
        const newValue = event.target.value;
        setInputType(newValue);
        setIsSaveDisabled(newValue === initialInputType);
        setIsNextDisabled(false);
    };

    const handleSave = async () => {
        if (!inputType) return;

        setLoading(true);
        setIsSaveDisabled(true);
        setErrorMessage("");

        try {
            const response = await addJobRole({
                variables: {
                    dartsFormNeeded: inputType === "Forms",
                    accountId,
                    jobroleId: roleId,
                    pocName,
                    pocEmail,
                    pocNumber,
                    numberLogin,
                    emailLogin,
                    mandatoryTimeDistribution,
                    targetEditDays,
                    inputEditDays,
                    name,
                    dartsNeeded,
                    clanNeeded,
                    syncNeeded,
                    geoTracking,
                    signInSignOutRequired,
                    locationRequired,
                    jobroleAccessUserIds,
                    ldmsNeeded,
                    mirrorNeeded,
                },
            });

            if (response?.data?.addJobrole?.success) {
                message.success("Jobrole updated successfully");
                setInitialInputType(inputType);
                setIsSaveDisabled(true);
            } else {
                setErrorMessage(response?.data?.addJobrole?.message || "Something went wrong!");
            }
        } catch (err) {
            setErrorMessage("Error occurred while saving data.");
            console.error("Error:", err);
        } finally {
            setLoading(false);
        }
    };

    const handleTargetFunnelNext = (data) => {
        setTargetData(data);
        setCurrentView("preview");
    };

    const handlePreviewBack = () => {
        setCurrentView("targetFunnel");
    };

    const handleMainNavigate = (viewName) => {
        setCurrentView(viewName);
    };

    const handleTargetPreviewNext = (data) => {
        setLeadIndicatorData(data);
        setCurrentView("leadIndicator");
    };

    const handleBack = () => {
        navigate("/admin-home");
    };

    const handleBackToViewRole = () => {
        navigate("/admin-view-role", {
            state: {
                roleId: roleId || location.state?.roleId,
                accountId: accountId || location.state?.accountId,
                accountName: accountName || location.state?.accountName,
                name: name || location.state?.name,
                dartsNeeded: inputType ? true : dartsNeeded || location.state?.dartsNeeded || dartsNeeded, // Update based on inputType
                clanNeeded: clanNeeded || location.state?.clanNeeded,
                syncNeeded: syncNeeded || location.state?.syncNeeded,
                dartsFormNeeded: inputType === "Forms" || dartsFormNeeded || location.state?.dartsFormNeeded,
                pocName: pocName || location.state?.pocName,
                pocEmail: pocEmail || location.state?.pocEmail,
                pocNumber: pocNumber || location.state?.pocNumber,
                numberLogin: numberLogin || location.state?.numberLogin,
                emailLogin: emailLogin || location.state?.emailLogin,
                mandatoryTimeDistribution: mandatoryTimeDistribution || location.state?.mandatoryTimeDistribution,
                targetEditDays: targetEditDays || location.state?.targetEditDays,
                inputEditDays: inputEditDays || location.state?.inputEditDays,
            },
        });
    };

    const handleDiscardClick = () => {
        navigate("/admin-home");
    };

    const renderView = () => {
        switch (currentView) {
            case "targetFunnel":
                return (
                    <AdminTargetFunnelConfiguration
                        onBack={() => setCurrentView("main")}
                        onNext={handleTargetFunnelNext}
                        roleId={roleId}
                        accountId={accountId}
                    />
                );
            case "preview":
                return (
                    <AdminTargetFunnelPreview
                        onBack={handlePreviewBack}
                        onNext={handleTargetPreviewNext}
                        targetData={targetData}
                        roleId={roleId}
                        accountId={accountId}
                    />
                );
            case "leadIndicator":
                return (
                    <AdminLeadIndicatorSelection
                        onBack={() => setCurrentView("preview")}
                        roleId={leadIndicatorData?.roleId}
                        targetFunnelName={leadIndicatorData?.targetFunnelName}
                    />
                );
            case "main":
            default:
                return (
                    <div className="user-management-container">
                        <PageBreadcrumb homePath="/admin">
                            <span onClick={handleBack} style={{ cursor: 'pointer' }}>{accountName}</span>
                            <span>&gt;</span>
                            <span onClick={handleBackToViewRole} style={{ cursor: 'pointer' }}>{name}</span>
                            <span>&gt;</span>
                            <span style={{ fontWeight: 'bold' }}>Darts</span>
                        </PageBreadcrumb>
                        <div>
                            <img
                                src={toggleImage01}
                                className="centered-image"
                                alt="Toggle Image"
                            />
                            <h2 className="product-config-title">Input Type Configuration</h2>

                            <div className="product-config-card">
                                <label className="product-config-option">
                                    <input
                                        type="radio"
                                        name="inputType"
                                        value="Forms"
                                        checked={inputType === "Forms"}
                                        onChange={handleInputChange}
                                    />
                                    <div
                                        className="radio-label"
                                        onClick={() => handleMainNavigate("targetFunnel")}
                                    >
                                        <div>
                                            <strong>Forms</strong>
                                            <p>Structured data input through forms</p>
                                        </div>
                                        <img src={NextIcon} alt="Next" className="next-icon" />
                                    </div>
                                </label>

                                <label className="product-config-option">
                                    <input
                                        type="radio"
                                        name="inputType"
                                        value="Numbers"
                                        checked={inputType === "Numbers"}
                                        onChange={handleInputChange}
                                    />
                                    <div
                                        className="radio-label"
                                        onClick={() => handleMainNavigate("targetFunnel")}
                                    >
                                        <div>
                                            <strong>Numbers</strong>
                                            <p>Simple numerical input forms</p>
                                        </div>
                                        <img src={NextIcon} alt="Next" className="next-icon" />
                                    </div>
                                </label>

                                <label
                                    className="product-config-option"
                                    onClick={() =>
                                        navigate("/admin-attendance-daily-norm", {
                                            state: {
                                                accountId,
                                                roleId,
                                                accountName,
                                                name,
                                                dartsFormNeeded, // Add this to preserve the value
                                                dartsNeeded,
                                                clanNeeded,
                                                syncNeeded,
                                                pocName,
                                                pocEmail,
                                                pocNumber,
                                                numberLogin,
                                                emailLogin,
                                                mandatoryTimeDistribution,
                                                targetEditDays,
                                                inputEditDays,
                                            },
                                        })
                                    }
                                >
                                    <div className="radio-label">
                                        <div>
                                            <strong>Attendance & Distribution</strong>
                                            <p>Simple numerical input forms</p>
                                        </div>
                                        <img src={NextIcon} alt="Next" className="next-icon" />
                                    </div>
                                </label>

                                <label
                                    className="product-config-option"
                                    onClick={() =>
                                        navigate("/admin-stages", {
                                            state: { accountId, roleId, accountName, name, dartsFormNeeded },
                                        })
                                    }
                                >
                                    <div className="radio-label">
                                        <div>
                                            <strong>LDMS Configuration</strong>
                                            <p>Define stages, statuses and activities</p>
                                        </div>
                                        <img src={NextIcon} alt="Next" className="next-icon" />
                                    </div>
                                </label>
                            </div>

                            {errorMessage && (
                                <div className="error-message">
                                    <p>{errorMessage}</p>
                                </div>
                            )}

                            <div className="product-config-buttons">
                                <button
                                    className={`product-config-btn ${isSaveDisabled ? "disabled-btn" : "enabled-btn"
                                        }`}
                                    onClick={handleSave}
                                    disabled={isSaveDisabled || loading}
                                >
                                    {loading ? <span className="inline-block w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin"></span> : "Save"}
                                </button>
                            </div>
                        </div>
                    </div>
                );
        }
    };

    return <div className="product-config-container">{renderView()}</div>;
};

export default AdminProductConfiguration;